import { useSnackbar } from "notistack";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { getErrorCode, getErrorStatusCode } from "../../api/api-response";
import { UserConverter } from "../../domain/converter/user.converter";
import { IUser } from "../../domain/user/user";
import { useGetLoggedInUserInfoQuery } from "../../repositories/queries/user/get-users-info.query";
import { useLoginAsQuery } from "../../repositories/queries/auth/mutation/login-as.query";
import { ILoginAsResponse } from "../../repositories/models/auth/login-as.response";
import { useLogoutAsQuery } from "../../repositories/queries/auth/mutation/logout-as.query";
import { useLogoutQuery } from "../../repositories/queries/auth/mutation/logout.query";
import { AuthUtil } from "../../utils/auth.util";
import { clearLogin, clearReturnUrl, isUserLoggedIn, setSession } from "../../utils/auth";
import { AvailableBaseRoutes } from "../../utils/constants";
import { useLoginAs } from "./login-as-context";
import { useUnsavedDataContext } from "../App/unsaved-data-context";

export interface IAuthProviderContextType {
  internalUser: IUser | undefined;
  userInitialRoute: string;
  getUser: () => Promise<IUser | undefined>;
  logout: () => Promise<void>;
  loginAs: (email: string) => Promise<void>;
  logoutAs: () => Promise<void>;
}

export const AuthProviderContext = createContext<IAuthProviderContextType>({} as IAuthProviderContextType);

interface IAuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = (props: IAuthProviderProps) => {
  const value = useAuthProvider();
  return <AuthProviderContext.Provider value={value}>{props.children}</AuthProviderContext.Provider>;
};

export const useAuthContext = () => {
  return useContext(AuthProviderContext);
};

const useAuthProvider = (): IAuthProviderContextType => {
  const [internalUser, setInternalUser] = useState<IUser | undefined>();
  const history = useHistory();
  const { setLoginAsTypes, setLoggedAs } = useLoginAs();
  const { setHasUnsavedData } = useUnsavedDataContext();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const { refetch: refetchUserInfo } = useGetLoggedInUserInfoQuery(false);
  const { mutateAsync: apiLogout } = useLogoutQuery();
  const { mutateAsync: loginAsUser } = useLoginAsQuery();
  const { mutateAsync: logoutAsUser } = useLogoutAsQuery();

  const getUser = useCallback(async (): Promise<IUser | undefined> => {
    if (!isUserLoggedIn()) {
      return undefined;
    }

    if (internalUser) {
      return internalUser;
    }

    try {
      const refetchedUserInfoResult = await refetchUserInfo();
      const userData = refetchedUserInfoResult.data;
      const user = userData?.user;
      const originalUser = userData?.originalUser;
      setInternalUser(user);

      if (originalUser) {
        setLoggedAs(true);
        const types = originalUser.group?.type ? [originalUser.group?.type] : undefined;
        setLoginAsTypes(types);
      }
      return user;
    } catch (error) {
      const errorMsg = getErrorCode(error);
      const errorCode = getErrorStatusCode(error);
      enqueueSnackbar(t("general.error_occurred", { errorCode, errorMsg }), { variant: "error" });
    }
  }, [enqueueSnackbar, internalUser, refetchUserInfo, setInternalUser, setLoggedAs, setLoginAsTypes, t]);

  useEffect(() => {
    getUser();
  }, [getUser]);

  const userInitialRoute = useMemo(() => {
    return internalUser ? AvailableBaseRoutes.Portal : AvailableBaseRoutes.Login;
  }, [internalUser]);

  const logout = async () => {
    // api call required to block the current jwt token
    await apiLogout();
    setInternalUser(undefined);
    clearLogin();
    clearReturnUrl();
    history.replace(AvailableBaseRoutes.Login);
  };

  const loginAs = async (email: string): Promise<void> => {
    let data: ILoginAsResponse | undefined;
    try {
      data = await loginAsUser({ email });
    } catch {
      // error already shown by the mutation's onError handler
      return;
    }
    if (!data) {
      return;
    }

    const { token, user } = data;
    setSession(token);
    const types = internalUser?.group?.type ? [internalUser.group.type] : [];
    const convertedUser = UserConverter.toDomain(user);

    setInternalUser(convertedUser);
    setLoginAsTypes(types);
    setLoggedAs(true);
    setHasUnsavedData(false);

    // clear query cache so no stale queries refetch with the old token
    queueMicrotask(() => {
      queryClient.clear();
      history.replace(AuthUtil.getRedirectUrl());
    });
  };

  const logoutAs = async (): Promise<void> => {
    const data = await logoutAsUser();
    const { token, user } = data;
    setSession(token);

    const convertedUser = UserConverter.toDomain(user);
    setInternalUser(convertedUser);
    setLoggedAs(false);
    setLoginAsTypes(undefined);
    setHasUnsavedData(false);

    // clear query cache so no stale queries refetch with the old token
    queueMicrotask(() => {
      queryClient.clear();
      history.replace(AvailableBaseRoutes.Portal);
    });
  };

  return {
    internalUser,
    userInitialRoute,
    getUser,
    logout,
    loginAs,
    logoutAs,
  };
};
